import React, { useState, useRef, useEffect } from "react";
import { Input, Button, Space, DatePicker, message } from "antd";
import dayjs from "dayjs";
import { SearchOutlined } from "@ant-design/icons";
import GenericTable from "../../../Components/GenericTable";
import GenericDatePicker from "../../../Components/GenericDatePicker";
import locationIcon from "../../../Assets/Icons/location.svg";
import GenericModal from "../../../Components/GenericModal";
import addIcon from "../../../Assets/Icons/add-btn.svg";
import { useNavigate } from "react-router-dom";
import "./style.css";
import { getShutDown } from "../../../API/User/shutdown";
import { useDateFilter } from "../../../Contexts/DateFilterContext";
import MapContainerComponent from "../../../Components/MapContainer";

const Shutdown = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [shutDown, setShutDown] = useState([]);
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const { date, setDate } = useDateFilter();
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [currentLat, setCurrentLat] = useState(null);
  const [currentLng, setCurrentLng] = useState(null);

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase())
        : "",
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    confirm();
  };

  const highlightText = (text) => {
    if (!searchText) return text;
    const regex = new RegExp(`(${searchText})`, "gi");
    const parts = String(text).split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "yellow",
            color: "#d32f2f",
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      ),
    );
  };
  const handleClick = () => {
    navigate("/add-shutdown");
  };

  const handleOk = () => {
    if (
      selectedVehicle?.latitude != null &&
      selectedVehicle?.longitude != null
    ) {
      const coords = `${selectedVehicle.latitude}, ${selectedVehicle.longitude}`;

      navigator.clipboard
        .writeText(coords)
        .then(() => {
          messageApi.success(`Coordinates copied: ${coords}`);
        })
        .catch((err) => {
          messageApi.error("Failed to copy coordinates.");
        });
    } else {
      messageApi.warning("No coordinates available for this vehicle.");
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
  };
  const shutDownColumns = [
    {
      title: "Vehicle",
      dataIndex: "vehicle_name",
      key: "vehicle_name",
      render: (text) => highlightText(text),
      ...getColumnSearchProps("vehicle_name"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",

      render: (status) => {
        const label = status
          ? status.charAt(0).toUpperCase() + status.slice(1)
          : "";

        return <span className={`status-badge ${status}`}>{label}</span>;
      },

      filters: [
        { text: "Manual", value: "manual" },
        { text: "Automatic", value: "automatic" },
      ],

      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Reason for Shutdown",
      dataIndex: "reason",
      key: "reason",
    },
    {
      title: "Date & Time",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      render: (date) => date,
    },
    {
      title: "Map",
      key: "map",
      render: (_, record) => (
        <a
          onClick={() => {
            setSelectedVehicle(record);
            setCurrentLat(record.latitude);
            setCurrentLng(record.longitude);
            setIsOpen(true);
          }}
        >
          <img src={locationIcon} alt="map preview" />
        </a>
      ),
    },
  ];

  const fetchData = async (selectedDate) => {
    try {
      setLoading(true);

      setShutDown([]);

      const formatted = selectedDate ? selectedDate.format("YYYY-MM-DD") : null;

      const response = await getShutDown(formatted, formatted);

      const arr = response?.data?.tracker || [];

      setShutDown(arr);
    } catch (err) {
      messageApi.error("Failed to fetch shutdown data.");
      setShutDown([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setDate(dayjs());
  }, []);

  useEffect(() => {
    if (date) {
      fetchData(date);
    }
  }, [date]);

  return (
    <div className="automated-shutdown">
      {contextHolder}
      <div className="button-wrapper">
        <div className="date-btn">
          <GenericDatePicker
            value={date}
            onChange={(val) => setDate(val ? dayjs(val) : null)}
          />
        </div>
        <div className="button">
          <Button type="default" onClick={handleClick} className="add-btn">
            <img src={addIcon} alt="add user" />
            Add Log
          </Button>
        </div>
      </div>
      <div className="table">
        <GenericTable dataSource={shutDown} columns={shutDownColumns} />
      </div>
      <div className="modal">
        <GenericModal
          title={selectedVehicle?.vehicle_name ?? "Vehicle Name"}
          open={isOpen}
          onOk={handleOk}
          onCancel={handleCancel}
          // cancelText="Shutdown Vehicle"
          // changed the shut down to close because the data in this table is coming from the api which is fetching data from the db table of suspected theft and the shut down already occurs on these vehicles
          cancelText="Close"
          okText="Copy Coordinates"
        >
          <MapContainerComponent latitude={currentLat} longitude={currentLng} vehicle_name={selectedVehicle?.vehicle_name} />

        </GenericModal>
      </div>
    </div>
  );
};

export default Shutdown;
